import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'
import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/utils/cn'

interface PhoneFieldInputProps {
  id?: string
  label?: string
  required?: boolean
  value: string
  error?: string | null
  options?: Record<string, unknown> | null
  compact?: boolean
  /** Render the error under the input (default). Off when the form shows it itself. */
  showError?: boolean
  /** Id of the element describing the error, for assistive technology. */
  describedBy?: string
  onChange: (value: string) => void
}

const DEFAULT_COUNTRY: CountryCode = 'US'

function displayName(country: CountryCode): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country) ?? country
  } catch {
    return country
  }
}

function asCountry(value: unknown): CountryCode | null {
  return typeof value === 'string' && /^[A-Z]{2}$/.test(value.toUpperCase())
    ? value.toUpperCase() as CountryCode
    : null
}

export function PhoneFieldInput({
  id,
  label,
  required,
  value,
  error,
  options,
  compact = false,
  showError = true,
  describedBy,
  onChange,
}: PhoneFieldInputProps) {
  const allowedCountries = useMemo(() => {
    const configured = Array.isArray(options?.allowedCountries)
      ? options.allowedCountries.map(asCountry).filter((country): country is CountryCode => !!country)
      : []
    return configured.length > 0 ? configured : getCountries()
  }, [options])

  const configuredCountry = asCountry(options?.defaultRegion ?? options?.country) ?? DEFAULT_COUNTRY
  const [country, setCountry] = useState<CountryCode>(
    allowedCountries.includes(configuredCountry) ? configuredCountry : allowedCountries[0] ?? DEFAULT_COUNTRY,
  )

  useEffect(() => {
    const parsed = value ? parsePhoneNumberFromString(value) : undefined
    if (parsed?.country && allowedCountries.includes(parsed.country)) setCountry(parsed.country)
  }, [value, allowedCountries])

  const parsed = value ? parsePhoneNumberFromString(value) : undefined
  const displayValue = parsed?.country === country ? parsed.formatNational() : value

  const handleCountryChange = (next: CountryCode) => {
    setCountry(next)
    if (!value) return
    const current = parsePhoneNumberFromString(value)
    if (current?.isValid()) onChange(current.number)
  }

  const handleValueChange = (next: string) => {
    const parsedValue = parsePhoneNumberFromString(next, country)
    onChange(parsedValue?.isValid() ? parsedValue.number : next)
  }

  return (
    <div className={compact ? 'flex min-w-0 flex-1 flex-col gap-1' : 'space-y-1'}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-neutral-700">
          {label}{required && <span className="ml-0.5 text-rose-500">*</span>}
        </label>
      )}
      <div className="flex min-w-0 gap-2">
        <select
          aria-label="Phone country"
          value={country}
          onChange={(event) => handleCountryChange(event.target.value as CountryCode)}
          className={cn(
            compact ? 'h-8 w-28 rounded-lg px-1.5 text-xs' : 'h-10 w-36 rounded-lg px-2 text-sm',
            'border bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400',
            error ? 'border-rose-300' : 'border-neutral-200',
          )}
        >
          {allowedCountries.map((entry) => (
            <option key={entry} value={entry}>
              {displayName(entry)} (+{getCountryCallingCode(entry)})
            </option>
          ))}
        </select>
        <input
          id={id}
          type="tel"
          inputMode="tel"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          value={displayValue}
          onChange={(event) => handleValueChange(event.target.value)}
          className={cn(
            compact ? 'h-8 px-2.5 text-sm' : 'h-10 px-3 text-sm',
            'min-w-0 flex-1 rounded-lg border bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-400',
            error ? 'border-rose-300' : 'border-neutral-200',
          )}
        />
      </div>
      {showError && error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  )
}